import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios'
import { ApiConfig } from '@/infrastructure/http/config'
import { NetworkError, TimeoutError, UnauthorizedError, ValidationError } from '@/core/shared/errors'

// Extend AxiosRequestConfig to include retry count
declare module 'axios' {
  interface AxiosRequestConfig {
    _retryCount?: number
  }
}

export interface HttpClient {
  get<T>(url: string, config?: AxiosRequestConfig): Promise<T>
  post<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T>
  put<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T>
  delete<T>(url: string, config?: AxiosRequestConfig): Promise<T>
  patch<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T>
}

export class AxiosHttpClient implements HttpClient {
  private readonly client: AxiosInstance
  private readonly config: ApiConfig
  
  constructor(
    config: ApiConfig,
    private getToken?: () => string | null
  ) {
    this.config = config
    this.client = axios.create({
      baseURL: config.baseUrl,
      timeout: config.timeout,
      headers: {
        'Content-Type': 'application/json',
      },
    })

    this.setupInterceptors()
  }

  private setupInterceptors(): void {
    // Request interceptor for adding auth token
    this.client.interceptors.request.use(
      (config) => {
        const token = this.getToken?.()
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }
        return config
      },
      (error) => Promise.reject(this.mapError(error))
    )

    // Response interceptor for error handling and retries
    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config

        // Don't retry if it's not a retryable error or if retries are exhausted
        if (
          !originalRequest ||
          originalRequest._retryCount >= this.config.retries ||
          !this.isRetryableError(error)
        ) {
          return Promise.reject(this.mapError(error))
        }

        // Increment retry count
        originalRequest._retryCount = (originalRequest._retryCount || 0) + 1

        // Calculate delay with exponential backoff
        const delay = this.config.retryDelay * Math.pow(2, originalRequest._retryCount - 1)

        // Wait before retrying
        await new Promise(resolve => setTimeout(resolve, delay))

        // Retry the request
        return this.client(originalRequest)
      }
    )
  }

  private isRetryableError(error: AxiosError): boolean {
    // Retry on network errors, timeouts, and 5xx server errors
    if (!error.response) {
      // Network error or timeout
      return true
    }

    const { status } = error.response
    // Retry on server errors (5xx) and gateway timeout (504)
    return status >= 500 || status === 504
  }

  private mapError(error: AxiosError): Error {
    if (error.code === 'ECONNABORTED') {
      return new TimeoutError('Request timeout')
    }

    if (!error.response) {
      return new NetworkError('Network error - no response received')
    }

    const { status, data } = error.response
    const errorData = data as { message?: string; field?: string }

    switch (status) {
      case 400:
        return new ValidationError(
          errorData?.message || 'Validation error',
          errorData?.field
        )
      case 401:
        return new UnauthorizedError(errorData?.message || 'Unauthorized')
      case 404:
        return new NetworkError('Resource not found', status)
      case 504:
        return new TimeoutError('Gateway timeout - server is temporarily unavailable')
      case 500:
        return new NetworkError('Internal server error', status)
      default:
        return new NetworkError(
          errorData?.message || `HTTP ${status} error`,
          status
        )
    }
  }

  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.client.get(url, config)
    return response.data
  }

  async post<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.client.post(url, data, config)
    return response.data
  }

  async put<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.client.put(url, data, config)
    return response.data
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.client.delete(url, config)
    return response.data
  }

  async patch<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.client.patch(url, data, config)
    return response.data
  }

  /**
   * Set auth token for future requests
   */
  setToken(token: string | null): void {
    if (token) {
      this.client.defaults.headers.Authorization = `Bearer ${token}`
    } else {
      delete this.client.defaults.headers.Authorization
    }
  }
}